import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type MfeModalPlaceholderCloseReason,
  showMfeModalPlaceholder
} from '@/common/Modals/MfeModalPlaceholder';
import {
  CTA_GUARD_ACTION,
  CtaGuard,
  CtaGuardOutcome
} from '~/constants/common/ctaGuard';
import {
  DEFAULT_MFE_MODAL_PLACEHOLDER_MESSAGE,
  DEFAULT_MFE_READY_FAILSAFE_MS,
  mfes
} from '~/constants/common/MfeModules';
import { useIsNative } from '~/context/common/DeviceContext';
import { useIsLoggedIn } from '~/context/common/UserContext';
import useMfeReady from '~/hooks/common/mfe/useMfeReady';
import { PLACEHOLDER_OVERLAP_MS } from '~/hooks/common/mfe/useOpenMfeModal';
import {
  ACCOUNT_MODAL_MODULE,
  selectIsMfeLoaded,
  selectMfeLoadCount,
  useMfeLoadStore
} from '~/state/common/MfeLoadState';
import { logInfo } from '~/utilities/loggers';

// Stable selectors — defined at module level for referential stability across renders.
// Readiness comes from useMfeReady, which is true only after ACCOUNT_MODAL_READY has fired
// (not merely 'loaded') — the header MFE's signal that CoreAccountModal is safe to call.
const selectIsAccountModalLoaded = selectIsMfeLoaded(ACCOUNT_MODAL_MODULE);
const selectAccountModalLoadCount = selectMfeLoadCount(ACCOUNT_MODAL_MODULE);

type Options = {
  // Shows the MFE placeholder modal, instead of the button's pending state, while a click waits.
  showPlaceholder?: boolean;
};

export default function useAccountModalReady({
  showPlaceholder
}: Options = {}) {
  const isLoggedIn = useIsLoggedIn();
  const isNative = useIsNative();
  const isReady = useMfeReady(mfes.accountModal);
  const accountModalLoaded = useMfeLoadStore(selectIsAccountModalLoaded);
  const accountModalLoadCount = useMfeLoadStore(selectAccountModalLoadCount);
  const hidePlaceholderRef = useRef<
    ((reason: MfeModalPlaceholderCloseReason) => void) | null
  >(null);
  const pendingRef = useRef<(() => void) | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [isPending, setIsPending] = useState(false);

  // Logged-in and native users never open the account modal, so they should
  // never wait for it. Only logged-out web users need deferred execution.
  const waitForAccountModal = !isLoggedIn && !isNative;

  const settle = useCallback((reason: MfeModalPlaceholderCloseReason) => {
    clearTimeout(timerRef.current);
    pendingRef.current = null;
    hidePlaceholderRef.current?.(reason);
    hidePlaceholderRef.current = null;
  }, []);

  useEffect(() => () => settle('cancelled'), [settle]);

  useEffect(() => {
    if (isReady && pendingRef.current) {
      logInfo(CTA_GUARD_ACTION, {
        guard: CtaGuard.Readiness,
        outcome: CtaGuardOutcome.DeferredResolved
      });

      const hidePlaceholder = hidePlaceholderRef.current;

      clearTimeout(timerRef.current);
      pendingRef.current();
      pendingRef.current = null;
      hidePlaceholderRef.current = null;
      setIsPending(false);

      if (hidePlaceholder) {
        setTimeout(() => hidePlaceholder('mfe-opened'), PLACEHOLDER_OVERLAP_MS);
      }
    }
  }, [isReady]);

  const whenReady = useCallback(
    (action: () => void) => {
      if (!waitForAccountModal || isReady) {
        action();
      } else {
        datadogRum.addAction('vdp-purchase-cta-click-deferred', {
          accountModalLoaded,
          accountModalLoadCount
        });

        logInfo(CTA_GUARD_ACTION, {
          accountModalLoaded,
          accountModalLoadCount,
          guard: CtaGuard.Readiness,
          outcome: CtaGuardOutcome.Deferred
        });

        settle('cancelled');
        pendingRef.current = action;

        if (showPlaceholder) {
          hidePlaceholderRef.current = showMfeModalPlaceholder({
            datadogContext: { mfe: 'accountModal' },
            message: DEFAULT_MFE_MODAL_PLACEHOLDER_MESSAGE,
            onDismiss: () => settle('user-dismissed')
          });
          timerRef.current = setTimeout(
            () => settle('timed-out'),
            DEFAULT_MFE_READY_FAILSAFE_MS
          );
        } else {
          setIsPending(true);
        }
      }
    },
    [
      accountModalLoadCount,
      accountModalLoaded,
      isReady,
      settle,
      showPlaceholder,
      waitForAccountModal
    ]
  );

  return { isPending: waitForAccountModal && isPending, isReady, whenReady };
}
